// Development Tools
// These utilities can be accessed via browser console for debugging and maintenance

import { db } from '../db/database'
import {
  findDuplicatePhotos,
  cleanupDuplicatePhotos,
  diagnosePendingPhotos,
  getPendingCount,
  clearAllPendingPhotos,
  deleteCorruptedPhoto
} from '../services/storageService'

// Make dev tools globally available in browser console
if (typeof window !== 'undefined') {
  window.clystcoreDevTools = {
    // Photo diagnostics
    async findDuplicates() {
      const report = await findDuplicatePhotos()
      console.log('=== DUPLICATE PHOTOS ANALYSIS ===')
      console.log(`Total photos: ${report.totalPhotos}`)
      console.log(`Uploaded: ${report.uploadedPhotos}`)
      console.log(`Pending: ${report.pendingPhotos}`)
      console.log(`Duplicate groups found: ${report.duplicateGroups}`)

      if (report.duplicates.length > 0) {
        console.log('\nDuplicate Groups:')
        console.table(report.duplicates)
      }

      if (report.suspiciousCategories.length > 0) {
        console.log('\nSuspicious Categories (>10 photos):')
        console.table(report.suspiciousCategories)
      }

      return report
    },

    async cleanupDuplicates(dryRun = true) {
      const report = await cleanupDuplicatePhotos(dryRun)
      console.log('=== CLEANUP REPORT ===')
      console.log(`Dry run: ${report.dryRun}`)
      console.log(`Would delete: ${report.wouldDelete} photos`)
      console.log(`Actually deleted: ${report.deleted} photos`)

      if (report.details.length > 0) {
        console.log('\nDetails:')
        console.table(report.details)
      }

      if (!dryRun) {
        console.log('\n✅ Cleanup complete! Refresh the page to see updated photo counts.')
      } else {
        console.log('\n💡 This was a dry run. Run cleanupDuplicates(false) to actually delete.')
      }

      return report
    },

    async diagnosePending() {
      const photos = await diagnosePendingPhotos()
      console.log(`=== PENDING PHOTOS DIAGNOSIS ===`)
      console.log(`Found ${photos.length} pending photos`)
      console.table(photos)
      return photos
    },

    async getPendingCount() {
      const count = await getPendingCount()
      console.log(`Pending photos: ${count}`)
      return count
    },

    async listAllPhotos() {
      const photos = await db.photos.toArray()
      console.log(`=== ALL PHOTOS (${photos.length} total) ===`)
      console.table(photos.map(p => ({
        id: p.id,
        surveyId: p.surveyId,
        category: p.categoryCode,
        uploaded: p.uploaded,
        hasImageData: !!p.imageData,
        remoteId: p.remoteId,
        r2Key: p.r2Key,
        capturedAt: p.capturedAt
      })))
      return photos
    },

    async clearAllPending() {
      if (!confirm('⚠️ WARNING: This will delete ALL pending photos that haven\'t been uploaded to R2. Are you sure?')) {
        console.log('Cancelled.')
        return
      }
      const report = await clearAllPendingPhotos()
      console.log(`=== CLEARED PENDING PHOTOS ===`)
      console.log(`Deleted: ${report.deleted}`)
      console.log(`Remaining: ${report.remaining}`)
      console.log('\n✅ Refresh the page to see updated counts.')
      return report
    },

    async deletePhoto(photoId) {
      if (!confirm(`Are you sure you want to delete photo ${photoId}?`)) {
        console.log('Cancelled.')
        return
      }
      await deleteCorruptedPhoto(photoId)
      console.log(`✅ Deleted photo ${photoId}. Refresh the page to see updates.`)
    },

    async findPhotosBySurvey(surveyId) {
      const photos = await db.photos.where('surveyId').equals(surveyId).toArray()
      console.log(`=== PHOTOS FOR SURVEY ${surveyId} (${photos.length} total) ===`)
      console.table(photos.map(p => ({
        id: p.id,
        category: p.categoryCode,
        uploaded: p.uploaded,
        hasImageData: !!p.imageData,
        remoteId: p.remoteId,
        r2Key: p.r2Key,
        notes: p.notes?.substring(0, 50) || '',
        capturedAt: p.capturedAt
      })))
      return photos
    },

    async findPhotosByCategory(surveyId, categoryCode) {
      const photos = await db.photos
        .where('surveyId')
        .equals(surveyId)
        .filter(p => p.categoryCode === categoryCode)
        .toArray()
      console.log(`=== PHOTOS FOR ${categoryCode} (${photos.length} total) ===`)
      console.table(photos.map(p => ({
        id: p.id,
        uploaded: p.uploaded,
        hasImageData: !!p.imageData,
        remoteId: p.remoteId,
        r2Key: p.r2Key,
        capturedAt: p.capturedAt
      })))
      return photos
    },

    help() {
      console.log(`
═══════════════════════════════════════════════════════════
          CLYSTCORE DEV TOOLS - Available Commands
═══════════════════════════════════════════════════════════

Photo Diagnostics:
------------------------------------------------------------------
  findDuplicates()                    Find all duplicate photos
  diagnosePending()                   List all pending photos
  getPendingCount()                   Get count of pending photos
  listAllPhotos()                     List all photos in IndexedDB

Photo Management:
------------------------------------------------------------------
  findPhotosBySurvey(surveyId)        List photos for a survey
  findPhotosByCategory(surveyId, cat) List photos for a category
  deletePhoto(photoId)                Delete a specific photo

Cleanup Operations:
------------------------------------------------------------------
  cleanupDuplicates(true)             Preview duplicate cleanup
  cleanupDuplicates(false)            Execute duplicate cleanup
  clearAllPending()                   ⚠️ Delete ALL pending photos

Examples:
------------------------------------------------------------------
  clystcoreDevTools.findDuplicates()
  clystcoreDevTools.findPhotosBySurvey(1)
  clystcoreDevTools.findPhotosByCategory(1, 'E2')
  clystcoreDevTools.cleanupDuplicates()  // dry run first
  clystcoreDevTools.cleanupDuplicates(false)  // actually delete

═══════════════════════════════════════════════════════════
      `)
    }
  }

  // Show help message on load (only in development)
  if (import.meta.env.DEV) {
    console.log(`
🛠️  Clystcore Dev Tools loaded!
   Type "clystcoreDevTools.help()" for available commands
    `)
  }
}

export default window.clystcoreDevTools
